import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { NumberInput } from '../ui/NumberInput';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';
import { Notice } from '../ui/Notice';

export const KITS_KEY = ['kits'] as const;

type Line = { itemId: string | null; childKitId: string | null; name: string; quantity: number };
type Expanded = { itemId: string; name: string; quantity: number; ownedQuantity: number; totalQuantity: number };
/** As the API sends it. */
/** `lines` and `expanded` are absent for a role that may not see the content of kits. */
export type Kit = { id: string; name: string; notes: string; lines?: Line[]; expanded?: Expanded[]; version: number };

/** A line being edited: the quantity stays text until it is saved. */
type DraftLine = { key: number; itemId: string | null; childKitId: string | null; name: string; quantity: string };
type Candidate = { id: string; name: string; isKit: boolean };

let nextKey = 1;
const toDraft = (l: Line): DraftLine => ({ key: nextKey++, itemId: l.itemId, childKitId: l.childKitId, name: l.name, quantity: String(l.quantity) });

export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Search box adding catalog items and other kits to the kit. */
function LinePicker({ kitId, onPick }: { kitId: string | null; onPick: (c: Candidate) => void }) {
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 250);
  const results = useQuery({
    queryKey: ['kits', 'picker', q],
    enabled: q !== '',
    queryFn: async (): Promise<Candidate[]> => {
      const param = `?q=${encodeURIComponent(q)}`;
      // A person may see kits but not the catalog (or the other way round): whatever is forbidden is simply not offered.
      const [items, nested] = await Promise.all([
        api<{ items: { id: string; name: string }[] }>(`/api/catalog/items${param}`).catch(() => ({ items: [] })),
        api<{ kits: { id: string; name: string }[] }>(`/api/kits${param}`).catch(() => ({ kits: [] })),
      ]);
      return [...items.items.map((i) => ({ id: i.id, name: i.name, isKit: false })), ...nested.kits.filter((k) => k.id !== kitId).map((k) => ({ id: k.id, name: k.name, isKit: true }))].slice(0, 15);
    },
  });
  return (
    <div>
      <label className="field">
        <span>Dodaj do zestawu</span>
        <input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder="Szukaj pozycji katalogu lub zestawu" autoComplete="off" />
      </label>
      {q !== '' && results.data && results.data.length === 0 && <p className="muted">Nic nie znaleziono.</p>}
      {results.data && results.data.length > 0 && (
        <ul className="list" aria-label="Wyniki wyszukiwania">
          {results.data.map((c) => (
            <li key={`${c.isKit}-${c.id}`} className="row">
              <div className="row-main">
                <strong>{c.name}</strong> <span className="chip">{c.isKit ? 'Zestaw' : 'Pozycja'}</span>
              </div>
              <button
                className="btn small"
                type="button"
                aria-label={`Dodaj: ${c.name}`}
                onClick={() => {
                  onPick(c);
                  setText('');
                }}
              >
                Dodaj
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** "What really goes out": the unfolded list of the saved kit with what is available now. */
function Preview({ kit }: { kit: Kit }) {
  if (!kit.expanded) return null;
  const expanded = kit.expanded;
  return (
    <section className="panel" aria-label="Skład zestawu" data-feature-id="kits.preview">
      <h3>Skład zestawu (co faktycznie jedzie)</h3>
      {expanded.length === 0 ? (
        <p className="muted">Zestaw jest pusty.</p>
      ) : (
        <ul className="list">
          {expanded.map((e) => (
            <li key={e.itemId} className="row">
              <div className="row-main">
                <strong>{e.name}</strong>
                <div className="muted">
                  Potrzeba: {e.quantity} · Dostępne teraz: {e.ownedQuantity}
                </div>
              </div>
              {e.quantity > e.ownedQuantity && <span className="chip sensitive">Brakuje: {e.quantity - e.ownedQuantity}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function KitForm({ initial, onClose }: { initial?: Kit; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { me } = useMe();
  const [base, setBase] = useState(initial);
  // Adding is offered only to people who may create; an existing kit is editable only with the edit right (otherwise: view only).
  const canEdit = !initial || (me !== undefined && can(me, 'kits.kits', 'edit'));
  // A role may be allowed to see kits but not their content: then lines are neither shown nor sent.
  const linesVisible = !initial || initial.lines !== undefined;
  const [name, setName] = useState(initial?.name ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [lines, setLines] = useState<DraftLine[]>((initial?.lines ?? []).map(toDraft));
  const [localError, setLocalError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = (kit: Kit) => {
    setBase(kit);
    setName(kit.name);
    setNotes(kit.notes);
    setLines((kit.lines ?? []).map(toDraft));
  };
  const parsed = lines.map((l) => ({ itemId: l.itemId, childKitId: l.childKitId, quantity: Number(l.quantity) }));
  const validQuantities = lines.every((l) => /^\d+$/.test(l.quantity.trim()) && Number(l.quantity) > 0);

  const save = useMutation({
    mutationFn: () => {
      const wire = parsed.map((l) => (l.itemId ? { itemId: l.itemId, quantity: l.quantity } : { childKitId: l.childKitId, quantity: l.quantity }));
      if (!base) return api<Kit>('/api/kits', { method: 'POST', body: { name, notes, lines: wire } });
      const original = JSON.stringify((base.lines ?? []).map((l) => ({ itemId: l.itemId, childKitId: l.childKitId, quantity: l.quantity })));
      const changed = {
        ...(name !== base.name ? { name } : {}),
        ...(notes !== base.notes ? { notes } : {}),
        ...(linesVisible && JSON.stringify(parsed) !== original ? { lines: wire } : {}),
      };
      return api<Kit>(`/api/kits/${base.id}`, { method: 'PATCH', body: { version: base.version, ...changed } });
    },
    onSuccess: async (kit) => {
      load(kit);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: KITS_KEY });
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  const conflict = error?.status === 409;

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    if (!validQuantities) {
      setLocalError('Ilość w każdym wierszu musi być liczbą całkowitą większą od zera.');
      return;
    }
    setLocalError('');
    save.mutate();
  }

  // owner 2026-10-23 21:14: "Zamknij", another kit or another screen with unsaved changes asks first
  const plain = (ls: { itemId: string | null; childKitId: string | null; quantity: string | number }[]) => ls.map((l) => ({ itemId: l.itemId, childKitId: l.childKitId, quantity: String(l.quantity).trim() }));
  const guardRef = useUnsavedGuard({
    dirty: canEdit && !sameValues({ name, notes, lines: plain(lines) }, { name: base?.name ?? '', notes: base?.notes ?? '', lines: plain(base?.lines ?? []) }),
    save: async (el) => {
      if (!validQuantities) {
        setLocalError('Ilość w każdym wierszu musi być liczbą całkowitą większą od zera.');
        return false;
      }
      if (!validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });

  function setQuantity(key: number, quantity: string) {
    setLines(lines.map((l) => (l.key === key ? { ...l, quantity } : l)));
  }

  return (
    <>
      <form ref={guardRef} className="panel form" aria-label={base ? `Edycja: ${base.name}` : 'Nowy zestaw'} data-feature-id="kits.edit" onSubmit={submit}>
        <h2>{base ? `Zestaw: ${base.name}` : 'Nowy zestaw'}</h2>
        {conflict && (
          <ConflictNotice
            onReload={() => {
              const current = error.current as Kit | undefined;
              void (current ? Promise.resolve(current) : api<Kit>(`/api/kits/${base!.id}`)).then(load);
              save.reset();
            }}
          />
        )}
        {error && !conflict && (
          <p className="alert" role="alert">
            {error.message}
            {fieldIssues(error).map((i) => (
              <span key={i.path + i.message}> {i.message}</span>
            ))}
          </p>
        )}
        {localError && (
          <p className="alert" role="alert">
            {localError}
          </p>
        )}
        {saved && !error && (
          <Notice onClose={() => setSaved(false)}>
            Zapisano.
          </Notice>
        )}
        <label className="field">
          <span>Nazwa</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" readOnly={!canEdit} />
        </label>
        <label className="field">
          <span>Uwagi</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} readOnly={!canEdit} />
        </label>

        {linesVisible && (
          <>
        <h3>Skład</h3>
        {lines.length === 0 && <p className="muted">Zestaw nie ma jeszcze pozycji. Wyszukaj poniżej pozycję katalogu lub inny zestaw.</p>}
        <ul className="list">
          {lines.map((l) => (
            <li key={l.key} className="row">
              <div className="row-main">
                <strong>{l.name}</strong> <span className="chip">{l.childKitId ? 'Zestaw' : 'Pozycja'}</span>
              </div>
              <label className="field">
                <span>Ilość: {l.name}</span>
                <NumberInput kind="integer" value={l.quantity} onValueChange={(v) => setQuantity(l.key, v)} readOnly={!canEdit} />
              </label>
              {canEdit && (
              <button className="btn small" type="button" aria-label={`Usuń z zestawu: ${l.name}`} onClick={() => setLines(lines.filter((x) => x.key !== l.key))}>
                Usuń
              </button>
              )}
            </li>
          ))}
        </ul>

        {canEdit && (
        <LinePicker
          kitId={base?.id ?? null}
          onPick={(c) => setLines([...lines, { key: nextKey++, itemId: c.isKit ? null : c.id, childKitId: c.isKit ? c.id : null, name: c.name, quantity: '1' }])}
        />
        )}
          </>
        )}

        <div className="actions">
          {canEdit && (
            <button className="btn primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj zestaw'}
            </button>
          )}
          <button className="btn" type="button" onClick={(e) => leaveGuarded(onClose, e.currentTarget.closest('form'))}>
            Zamknij
          </button>
        </div>
      </form>
      {base && <Preview kit={base} />}
    </>
  );
}

/** Add (no `kitId`) or edit a kit; the saved kit's unfolded content is shown under the form. */
export function KitEditor({ kitId, onClose }: { kitId?: string; onClose: () => void }) {
  const detail = useQuery({ queryKey: [...KITS_KEY, 'detail', kitId], enabled: kitId !== undefined, gcTime: 0, queryFn: () => api<Kit>(`/api/kits/${kitId}`) });
  if (kitId === undefined) return <KitForm onClose={onClose} />;
  if (detail.error) {
    return (
      <p className="alert" role="alert">
        {detail.error instanceof ApiError ? detail.error.message : 'Nie udało się wczytać zestawu'}
      </p>
    );
  }
  if (!detail.data) return <p className="muted" role="status">Wczytywanie…</p>;
  return <KitForm key={detail.data.id} initial={detail.data} onClose={onClose} />;
}
